import React, { useEffect, useRef, useState } from 'react'
import { i18n } from '@/locale/i18n'
import Chip from '@/components/ui/chip'
import ScrollArrow from '@/components/ui/scroll-arrow/ScrollArrow'

// Home-only tags strip, rendered below the feed H1 (instead of the full-width
// subcategories row inside CategoryNavigation) so the right rail can occupy
// the space next to it.
const SCROLL_STEP = 320

const HomeTagsRow = ({
  subcategories = [],
  loading = false,
  fullPath = '',
  onTagSelect,
  activeTag,
  showAllChip = true,
  /** A refinement the strip's own list does not carry (a `?tag=` on the "All"
   *  view), shown as a removable active chip so an active filter is never
   *  invisible. */
  refinementChip = null,
}) => {
  const scrollRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const hasTags = subcategories.length > 0 || Boolean(refinementChip)

  const updateArrows = () => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 2)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2)
  }

  useEffect(() => {
    updateArrows()
    // Re-measure on the next frame: on first load fonts/layout may not be
    // settled yet, and the arrow must show before any scroll happens.
    const raf = requestAnimationFrame(updateArrows)
    window.addEventListener('resize', updateArrows)
    let observer
    const el = scrollRef.current
    if (el && typeof ResizeObserver !== 'undefined') {
      // Fires once on observe (post-layout) and on any later size change
      observer = new ResizeObserver(updateArrows)
      observer.observe(el)
    }
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', updateArrows)
      if (observer) observer.disconnect()
    }
  }, [subcategories.length, loading])

  // Brings the selected chip into view only when it is off the visible area —
  // a chip already on screen stays where it was clicked, so selecting one never
  // slides the strip under the cursor. The key comes from `?tag=<slug>`, so it
  // is escaped: a quote or bracket in it would throw out of the selector.
  useEffect(() => {
    const el = scrollRef.current
    const key = String(activeTag ?? '__all')
    const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(key) : key
    const chip = el?.querySelector(`[data-key="${escaped}"]`)
    if (!el || !chip) return

    const chipBox = chip.getBoundingClientRect()
    const viewBox = el.getBoundingClientRect()
    if (chipBox.left >= viewBox.left - 1 && chipBox.right <= viewBox.right + 1) return

    chip.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
  }, [activeTag])

  if (!hasTags && !loading) return null

  const scrollByStep = direction => {
    scrollRef.current?.scrollBy({ left: direction * SCROLL_STEP, behavior: 'smooth' })
  }

  const renderSkeleton = () => (
    <div className="flex gap-2 overflow-x-auto scrollbar-hide">
      {[72, 90, 60, 85, 55, 70].map((w, index) => (
        <div
          key={`home-tag-skeleton-${index}`}
          className="relative overflow-hidden h-8 px-4 rounded-lg bg-surface-2 flex-shrink-0 flex items-center">
          <div className="h-4 rounded" style={{ width: `${w}px` }}></div>
          <div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-Neutral-3/20 to-transparent"
            style={{ animation: 'shimmer 1.5s infinite', transform: 'translateX(-100%)' }}
          />
        </div>
      ))}
    </div>
  )

  const pills = [
    showAllChip && (
      <Chip
        key="home-tag-all"
        text={i18n('markets.category_all')}
        value="__all"
        active={!activeTag && !refinementChip}
        onClick={() => onTagSelect && onTagSelect(null)}
      />
    ),
    refinementChip && (
      <Chip
        key={`home-tag-refinement-${refinementChip.value}`}
        text={refinementChip.text}
        value={refinementChip.value}
        active
        onClick={() => refinementChip.onClear()}
      />
    ),
    ...subcategories.map(category => {
      /* Chips are named by slug where the category has one: the selection is
         URL state (`?topic=<slug>`, `?tag=<slug>`), and a numeric id in a
         shared link says nothing and breaks if ids are reseeded. */
      const identifier = category.slug || category.id
      return (
        <Chip
          key={`home-tag-${category.id}`}
          text={category.title}
          value={identifier}
          active={activeTag === identifier}
          onClick={id => onTagSelect && onTagSelect(id)}
        />
      )
    }),
  ].filter(Boolean)

  return (
    <div className="view-feed__tags-row min-h-12 px-4 md:px-[30px] pb-4">
      {!hasTags && loading ? (
        renderSkeleton()
      ) : (
        <div className="relative">
          {canScrollLeft && <ScrollArrow direction={-1} onClick={() => scrollByStep(-1)} />}
          <div
            ref={scrollRef}
            onScroll={updateArrows}
            className="flex gap-2 overflow-x-auto scrollbar-hide"
            style={{ WebkitOverflowScrolling: 'touch' }}>
            {pills}
          </div>
          {canScrollRight && <ScrollArrow direction={1} onClick={() => scrollByStep(1)} />}
        </div>
      )}
    </div>
  )
}

export default HomeTagsRow
